import React, { useState } from 'react';

import { Form, Formik } from 'formik';
import { useToasts } from 'react-toast-notifications';

import Button from '@components/common/Button/Button';
import Input from '@components/common/Formik/FormikInputField';
import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import Modal from '@components/common/Modals/Modal/Modal';
import Text from '@components/common/Text/Text';
import DescriptionGenerationBlockedModal from '@components/lpc/Pot/CreatePotLayout/PoolsTunnel/SolidarityPoolsTunnel/DescriptionGenerationBlockedModal/DecriptionGenerationBlockedModal';
import potService from '@services/domain/Lpc/PotService';
import { generateDescriptionSchema } from '@validations/potSchema';

import css from './DescriptionGeneratorModal.module.scss';

type DescriptionGeneratorModalProps = {
    isVisible: boolean;
    onHide: (boolean) => void;
    title: string;
    category: string;
    onAppendDescription: (description: string) => void;
};

const DescriptionGeneratorModal = ({
    isVisible,
    onHide,
    title,
    category,
    onAppendDescription,
}: DescriptionGeneratorModalProps) => {
    const { t } = useTranslateLpc(['lpc-create-pot', 'common']);
    const { addToast } = useToasts();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isGenerationBlocked, setIsGenerationBlocked] = useState<boolean>(false);

    const getInitialValues = () => {
        return {
            description: '',
        };
    };

    const onSubmit = (values: { description: string }) => {
        setIsLoading(true);
        potService
            .generateDescription({ title: title, category: category, ...values })
            .then((res) => {
                onAppendDescription(res.data?.replaceAll('\n', '<br>'));
                onHide(true);
            })
            .catch((error) => {
                if (error.status === 429) {
                    setIsGenerationBlocked(true);
                } else {
                    addToast(t(`errors.CHAT_GPT_NOT_AVAILABLE`, { ns: 'common' }), {
                        appearance: 'error',
                        autoDismiss: true,
                    });
                }
            })
            .finally(() => setIsLoading(false));
    };

    return (
        <Modal
            visible={isVisible}
            hideCloseButton
            onHide={() => onHide(false)}
            customClass={css.description_generator_modal}
            customOverlay={css.description_generator_modal__container}
        >
            <div>
                <div className={css.header}>
                    <h5>{t('form.solidarity-pool.ai-editor.title')}</h5>
                    <Text customClass={css.header__hint} variant={'caption_01'} color={'txt-primary'}>
                        {t('form.solidarity-pool.ai-editor.label')}
                    </Text>
                </div>
                <DescriptionGenerationBlockedModal
                    isVisible={isGenerationBlocked}
                    onHide={() => setIsGenerationBlocked(false)}
                />
                <Formik
                    initialValues={getInitialValues()}
                    validationSchema={generateDescriptionSchema}
                    onSubmit={(values) => onSubmit(values)}
                >
                    {() => (
                        <Form className={css.description_generator_modal__form}>
                            <div className={css.description_generator_modal__form__content}>
                                <div className={css.description_generator_modal__form__input}>
                                    <Input
                                        placeholder={t('form.solidarity-pool.description-placeholder')}
                                        name="descriptionPrompt"
                                        type="textarea"
                                    />
                                </div>

                                {/* SUBMIT */}
                                <div className={css.description_generator_modal__form__submit}>
                                    <Button
                                        type={'button'}
                                        customClass={css.description_generator_modal__form__submit__cancel}
                                        variant={'white'}
                                        onClick={() => {
                                            onHide(false);
                                        }}
                                    >
                                        {t('form.solidarity-pool.ai-editor.cancel')}
                                    </Button>
                                    <Button
                                        type={'submit'}
                                        variant={'primary'}
                                        customClass={css.description_generator_modal__form__submit__generate}
                                        icon={'/front-static/icons/pot/stars-white.svg'}
                                        isLoading={isLoading}
                                        isDisabled={isLoading}
                                    >
                                        {t('form.solidarity-pool.ai-editor.generate')}
                                    </Button>
                                    <input type="hidden" autoFocus={true} />
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </Modal>
    );
};

export default DescriptionGeneratorModal;
